import { useCallback, useEffect, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AddIcon from '@mui/icons-material/Add';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import {
  isVideoUrl,
  isVideoMimeType,
  withGifLoop,
} from '../../../utils/videoUtils';
import { SectionFieldType } from '../../../api/public';
import useAppContext from '../../../hooks/useAppContext';
import { AlertType } from '../../../design-system/DSAlert';
import {
  MAX_IMAGE_SIZE_BYTES,
  MAX_VIDEO_SIZE_BYTES,
} from '../../../consts/general';

type MediaUploadZoneProps = {
  label: string;
  subtitle?: string;
  mediaUrl?: string;
  isLoading?: boolean;
  onFileSelected?: (file: File) => void;
  mediaType?: SectionFieldType.IMAGE | SectionFieldType.VIDEO;
  minHeight?: string;
  maxHeight?: string;
  accept?: string;
  aspectRatio?: string;
  readOnly?: boolean;
};

const MediaUploadZone = ({
  label,
  subtitle,
  mediaUrl,
  isLoading,
  onFileSelected,
  mediaType = SectionFieldType.IMAGE,
  minHeight: minHeightProp,
  maxHeight: maxHeightProp,
  accept,
  aspectRatio,
  readOnly,
}: MediaUploadZoneProps) => {
  const { spacing, palette } = useTheme();
  const { showAlert } = useAppContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [localFileType, setLocalFileType] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [mediaType, mediaUrl, localPreviewUrl, localFileType]);

  const togglePlayPause = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {
        // Playback can fail due to browser autoplay policies.
        // The play/pause event listeners keep isPlaying in sync.
      });
    } else {
      video.pause();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const isVideo = isVideoMimeType(file.type);
      const maxSize = isVideo ? MAX_VIDEO_SIZE_BYTES : MAX_IMAGE_SIZE_BYTES;
      const maxLabel = isVideo ? '70MB' : '10MB';
      if (file.size > maxSize) {
        showAlert({
          message: `File size exceeds the limit. Max size is ${maxLabel}.`,
          alertType: AlertType.ERROR,
        });
        event.target.value = '';
        return;
      }
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
      setLocalPreviewUrl(URL.createObjectURL(file));
      setLocalFileType(file.type);
      onFileSelected?.(file);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  // Keep the local blob preview while it exists to avoid video reload glitch.
  // Only fall back to the remote URL when there's no local preview (e.g. page load).
  const displayUrl =
    localPreviewUrl || (mediaUrl ? withGifLoop(mediaUrl) : null);

  const shouldRenderAsVideo =
    (localFileType && isVideoMimeType(localFileType)) ||
    (mediaType === SectionFieldType.VIDEO &&
      displayUrl &&
      !localPreviewUrl &&
      isVideoUrl(displayUrl));

  const mediaStyle = {
    width: '100%',
    maxHeight: maxHeightProp ?? '100%',
    objectFit: 'cover' as const,
    borderRadius: spacing(2),
  };

  const renderContent = () => {
    if (isLoading && !displayUrl) {
      return <DSCircularProgress size={32} sx={{ color: 'text.secondary' }} />;
    }

    if (displayUrl && shouldRenderAsVideo) {
      return (
        <DSBox
          onClick={(event: React.MouseEvent) => event.preventDefault()}
          sx={{
            position: 'relative',
            width: '100%',
            '&:hover .video-play-pause-btn': {
              opacity: 1,
            },
          }}
        >
          <video
            ref={videoRef}
            src={displayUrl}
            loop
            muted
            playsInline
            style={mediaStyle}
          />
          <DSBox
            className="video-play-pause-btn"
            onClick={togglePlayPause}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xxs: '56px', sm: '116px' },
              height: { xxs: '56px', sm: '116px' },
              borderRadius: '50%',
              backgroundColor: alpha(palette.common.white, 0.56),
              boxShadow: 24,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              opacity: isPlaying ? 0 : 1,
            }}
          >
            {isPlaying ? (
              <PauseIcon
                sx={{
                  fontSize: { xxs: 24, sm: 50 },
                  color: 'common.black',
                  fill: palette.common.black,
                  stroke: palette.common.black,
                  strokeWidth: '4.221px',
                }}
              />
            ) : (
              <PlayArrowIcon
                sx={{
                  fontSize: { xxs: 24, sm: 50 },
                  color: 'common.black',
                  fill: palette.common.black,
                  stroke: palette.common.black,
                  strokeWidth: '4.221px',
                  ml: '4px',
                }}
              />
            )}
          </DSBox>
        </DSBox>
      );
    }

    if (displayUrl) {
      return <img src={displayUrl} alt="Uploaded media" style={mediaStyle} />;
    }

    return (
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <AddIcon
          sx={{ fontSize: 32, color: alpha(palette.common.white, 0.4) }}
        />
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <DSTypography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            {label}
          </DSTypography>
          {subtitle && (
            <DSTypography
              variant="body2"
              sx={{ color: 'action.active', textAlign: 'center' }}
            >
              {subtitle}
            </DSTypography>
          )}
        </DSBox>
      </DSBox>
    );
  };

  return (
    <DSBox
      {...(!readOnly && { component: 'label' })}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
        minHeight: minHeightProp ?? spacing(20),
        ...(maxHeightProp && { maxHeight: maxHeightProp }),
        width: '100%',
        ...(aspectRatio && { aspectRatio }),
        backgroundColor: displayUrl
          ? alpha(palette.common.white, 0.04)
          : 'transparent',
        border: displayUrl ? 'none' : `3px dashed ${palette.white[12]}`,
        borderRadius: spacing(2),
        ...(!readOnly && { cursor: 'pointer' }),
        overflow: 'hidden',
        transition: 'border-color 0.2s, background-color 0.2s',
        ...(!readOnly && {
          '&:hover': displayUrl
            ? { opacity: 0.8 }
            : {
                borderColor: alpha(palette.common.white, 0.35),
                backgroundColor: alpha(palette.common.white, 0.06),
              },
        }),
      }}
    >
      {renderContent()}
      {!readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          hidden
          onChange={handleFileChange}
        />
      )}
    </DSBox>
  );
};

export default MediaUploadZone;
